// This file is used to generate the fields for the different accordions in the modal
// that displays the differences between the master and the branch.
// Each accordion is created by the function createAccordionData
// Each field is created by the function createFieldData
// The function generateAccordionForTags, generateAccordionForEvents and generateAccordionForRules and generateAccordionConfig
// are used to generate the accordions for the tags, events, rules and the config of the container respectively

import { toNumber, isFinite } from 'lodash';
import moment from 'moment';
import { store } from '@store';

const STATUS = {
  CREATED: 'created',
  UPDATED: 'updated',
  DELETED: 'deleted',
  TRASHED: 'trashed',
  ADDED: 'added',
};

// check if a value is a number, usefull to check if a value is a 0 or a 1 but not a undefined or a null
const isValidNumber = value => {
  return isFinite(toNumber(value));
};

// generate the data scruture used to display an accordion in the modal
const createAccordionData = function({
  title,
  statuses: { master: statusMaster, branch: statusBranch },
  payloadMerge: { masterMerge, branchMerge, masterRefuse, branchRefuse },
  elementType,
  fields = [],
}) {
  // check values
  if (title === null || title === undefined || title === '') {
    console.error('accordion title is required');
  }
  if (fields === null || fields === undefined || !Array.isArray(fields)) {
    console.error('fields is required and must be an array');
  }
  const VALID_STATUSES = [STATUS.CREATED, STATUS.UPDATED, STATUS.DELETED];
  if (typeof statusMaster === 'string' && VALID_STATUSES.indexOf(statusMaster) === -1) {
    console.error('statusMaster doesnt have a valid status:', `"${statusMaster}"`, title);
  }
  if (typeof statusBranch === 'string' && VALID_STATUSES.indexOf(statusBranch) === -1) {
    console.error('statusBranch doesnt have a valid status:', `"${statusBranch}"`, title);
  }

  return {
    title, // title of the accordion if the header
    statuses: { master: statusMaster, branch: statusBranch }, // status of the master and the branch
    payloadMerge: { elementType, masterMerge, branchMerge, masterRefuse, branchRefuse }, // payload to merge the master and the branch
    fields, // fields of the accordion that describe the differences between the master and the branch
    id_accordion: Math.random() // unique id of the accordion use to toggle the accordion
      .toString(36)
      .substring(7),
  };
};

// fields are both the left and right side of a value comparaison between the master and the branch
// each field type is defined in field-branch-change.vue
const createFieldData = function({ label, type, master, branch }) {
  return {
    label, // label of the field
    type, // type of the field (text, checkbox, list, map, code, map-checkbox)
    master, // value of the master side ( left side ). if null the field and it's label will not be displayed
    branch, // value of the branch side ( right side ). if null the field and it's label will not be displayed
  };
};

// the API always return the last action of the element, this created edge case for the element's ( tags, rules, events) status
const getStatusFromAction = function(actionA, actionB, elementBExist) {
  // this condition is a durty fix, the API should return the correct status of the element
  // if the element A was updated and the element B doesn't exist, it means that the element A was created
  if ((actionA === STATUS.UPDATED || actionA === STATUS.TRASHED) && !elementBExist && actionB !== STATUS.DELETED) {
    return STATUS.CREATED;
  }

  // this condition is a durty fix, the API should return the correct status of the element
  // if element A was added and element B exist, it means that the element A was updated
  if ((actionA === STATUS.ADDED || actionA === STATUS.CREATED) && elementBExist) {
    return STATUS.UPDATED;
  }

  // if the element A was added, we display it as created
  if (actionA === STATUS.ADDED) {
    return STATUS.CREATED;
  }

  // if the element A was trashed, we display it as updated
  if (actionA === STATUS.TRASHED) {
    return STATUS.UPDATED;
  }

  //if element A doesn't have a status but element B doesn't exist, it means that the element A was created
  if (actionA === '' && !elementBExist) {
    return STATUS.CREATED;
  }
  return actionA;
};

const handleTryCatch = fun => {
  try {
    fun();
  } catch (e) {
    console.error(e);
  }
};

// generate the payload to merge either the branch is the master or the master is the branch
// this payload is only used to merge the elements tags, rules, events but not the config which is a special case
const getMergePayloadForElementWithIDs = ({ statusMaster, statusBranch, idMaster, idBranch }) => {
  const getPayloadMerge = (status, idA, idB) => {
    switch (status) {
      case STATUS.DELETED:
        return { action: 'delete', id: idB };
      case STATUS.UPDATED:
        return { action: 'update', source_id: idA, id: idB };
      case STATUS.CREATED:
        return { action: 'create', source_id: idA };
      default:
        return null;
    }
  };

  const payload = { masterMerge: {}, branchMerge: {}, masterRefuse: {}, branchRefuse: {} };

  payload.masterMerge = getPayloadMerge(statusMaster, idMaster, idBranch);
  payload.branchMerge = getPayloadMerge(statusBranch, idBranch, idMaster);
  payload.masterRefuse = { action: 'refuse_merge', id: idMaster };
  payload.branchRefuse = { action: 'refuse_merge', id: idBranch };
  return payload;
};

export const generateAccordionForTags = function(data, t) {
  const accordions = [];

  // for each tag we create an accordion
  data?.tag?.forEach(tag => {
    handleTryCatch(() => {
      const tagMaster = tag.master?.id_tag ? tag.master : null; // only tag with an id_tag are considered, the API should return only tag with an id_tag but it's an extra check
      const tagBranch = tag.branch?.id_tag ? tag.branch : null; // only tag with an id_tag are considered, the API should return only tag with an id_tag but it's an extra check

      const statusMaster = getStatusFromAction(tagMaster?.action, tagBranch?.action, Boolean(tagBranch?.id_tag));
      const statusBranch = getStatusFromAction(tagBranch?.action, tagMaster?.action, Boolean(tagMaster?.id_tag));
      const accordionData = createAccordionData({
        title: `${t('diffModal.tags.label')}: ${tagBranch?.label_tag ?? tagMaster?.label_tag}`,
        elementType: 'tag',
        statuses: {
          master: statusMaster,
          branch: statusBranch,
        },
        payloadMerge: getMergePayloadForElementWithIDs({
          statusMaster,
          statusBranch,
          idMaster: tagMaster?.id_tag,
          idBranch: tagBranch?.id_tag,
        }),
      });

      //activation
      if (isValidNumber(tagBranch?.disabled) || isValidNumber(tagMaster?.disabled)) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.activation'),
            type: 'checkbox',
            master: tagMaster?.disabled === 0 ? true : tagMaster?.disabled === 1 ? false : null,
            branch: tagBranch?.disabled === 0 ? true : tagBranch?.disabled === 1 ? false : null,
          })
        );
      }

      //timeout
      if (tagBranch?.tagkilling || tagMaster?.tagkilling) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.timeout'),
            type: 'text',
            master: tagMaster?.tagkilling,
            branch: tagBranch?.tagkilling,
          })
        );
      }

      //title
      //Element's label are always returned by the API sp that it's can be displayed in the header of the accordion
      //We need to check if they are different so that there are not displayed in the fields
      if ((tagBranch?.label_tag || tagMaster?.label_tag) && tagBranch?.label_tag !== tagMaster?.label_tag) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.name'),
            type: 'text',
            master: tagMaster?.label_tag,
            branch: tagBranch?.label_tag,
          })
        );
      }

      //variables_mapping
      if (tagBranch?.variables || tagMaster?.variables) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.variables_mapping'),
            type: 'map',
            master: tagMaster?.variables?.map(variable => ({
              key: variable.code,
              value: `value:${variable.variable}${
                variable.processingFunctions.length > 0 ? ', Operations:' : ''
              } ${variable.processingFunctions
                .map(operation => {
                  return operation.name;
                })
                .join(', ')}`,
            })),
            branch: tagBranch?.variables?.map(variable => ({
              key: variable.code,
              value: `value:${variable.variable}${
                variable.processingFunctions.length > 0 ? ', Operations:' : ''
              } ${variable.processingFunctions
                .map(operation => {
                  return operation.name;
                })
                .join(', ')}`,
            })),
          })
        );
      }

      // code
      if (tagBranch?.code_tag || tagMaster?.code_tag) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.code'),
            type: 'code',
            master: tagMaster?.code_tag,
            branch: tagBranch?.code_tag,
          })
        );
      }

      //noscript_code
      if (tagBranch?.noscript_tag || tagMaster?.noscript_tag) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.noscript'),
            type: 'code',
            master: tagMaster?.noscript_tag,
            branch: tagBranch?.noscript_tag,
          })
        );
      }

      //triggers
      const triggersMaster = tagMaster?.triggers?.map(trigger => trigger.label).sort();
      const triggersBranch = tagBranch?.triggers?.map(trigger => trigger.label).sort();

      if (
        JSON.stringify(triggersMaster) !== JSON.stringify(triggersBranch) &&
        (triggersMaster?.length > 0 || triggersBranch?.length > 0)
      ) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.triggers'),
            type: 'list',
            master: triggersMaster,
            branch: triggersBranch,
          })
        );
      }

      //perimeters
      const perimetersBranch = tagBranch?.rules?.filter(
        rule => rule.perimeter === 1 && rule.bin !== 1 && Boolean(rule.parent_id)
      );
      const perimetersMaster = tagMaster?.rules?.filter(
        rule => rule.perimeter === 1 && rule.bin !== 1 && Boolean(rule.id_rule)
      );

      const perimetersMasterFiltered = perimetersMaster?.filter(
        rule => !perimetersBranch?.some(perimeter => perimeter.parent_id === rule.id_rule)
      );
      const perimetersBranchFiltered = perimetersBranch?.filter(
        rule => !perimetersMaster?.some(perimeter => perimeter.id_rule === rule.parent_id)
      );
      // we only display perimeters if there is a change in assignasion of perimeters between master and branch
      // if there is a change, we display all perimeters so that the user can see the changes in relation to other assigned perimeters
      const didAnyAssignationChange = perimetersBranchFiltered?.length > 0 || perimetersMasterFiltered?.length > 0;

      const perimetersBranchLabel = perimetersBranch?.map(perimeter => perimeter.label_rule);
      const perimetersMasterLabel = perimetersMaster?.map(perimeter => perimeter.label_rule);
      if (didAnyAssignationChange) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.perimeters'),
            type: 'list',
            master: perimetersMasterLabel,
            branch: perimetersBranchLabel,
          })
        );
      }
      //constraints
      const constraintsBranch = tagBranch?.rules?.filter(
        rule => rule.perimeter !== 1 && rule.bin !== 1 && Boolean(rule.parent_id)
      );
      const constraintsMaster = tagMaster?.rules?.filter(
        rule => rule.perimeter !== 1 && rule.bin !== 1 && Boolean(rule.id_rule)
      );

      const constraintsBranchFiltered = constraintsBranch?.filter(
        rule => !constraintsMaster?.some(constraint => constraint.id_rule === rule.parent_id)
      );
      const constraintsMasterFiltered = constraintsMaster?.filter(
        rule => !constraintsBranch?.some(constraint => constraint.parent_id === rule.id_rule)
      );

      // we only display constraints if there is a change in assignasion of constraints between master and branch
      // if there is a change, we display all constraints so that the user can see the changes in relation to other assigned constraints
      const didAnyConstraintChange = constraintsBranchFiltered?.length > 0 || constraintsMasterFiltered?.length > 0;

      const constraintsBranchLabel = constraintsBranch?.map(constraint => constraint.label_rule);
      const constraintsMasterLabel = constraintsMaster?.map(constraint => constraint.label_rule);
      if (didAnyConstraintChange) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.constraints'),
            type: 'list',
            master: constraintsMasterLabel,
            branch: constraintsBranchLabel,
          })
        );
      }

      if (tagMaster?.privacy?.length > 0 || tagBranch?.privacy?.length > 0) {
        // privacy category
        const doesMasterPrivacyCategoryExist = tagMaster?.privacy?.length > 0 && tagMaster?.privacy[0]?.category?.label;
        const masterPrivacyCategory = doesMasterPrivacyCategoryExist ? tagMaster?.privacy[0]?.category?.label : null;

        const doesBranchPrivacyCategoryExist = tagBranch?.privacy?.length > 0 && tagBranch?.privacy[0]?.category?.label;
        const branchPrivacyCategory = doesBranchPrivacyCategoryExist ? tagBranch?.privacy[0]?.category?.label : null;
        if ((masterPrivacyCategory || branchPrivacyCategory) && masterPrivacyCategory !== branchPrivacyCategory) {
          accordionData.fields.push(
            createFieldData({
              label: t('diffModal.tags.privacy_category'),
              type: 'text',
              master: masterPrivacyCategory,
              branch: branchPrivacyCategory,
            })
          );
        }

        //privacy vendor
        const doesMasterPrivacyVendorExist = tagMaster?.privacy?.length > 0 && tagMaster?.privacy[0]?.vendor?.label;
        const masterPrivacyVendor = doesMasterPrivacyVendorExist ? tagMaster?.privacy[0]?.vendor?.label : null;
        const doesBranchPrivacyVendorExist = tagBranch?.privacy?.length > 0 && tagBranch?.privacy[0]?.vendor?.label;
        const branchPrivacyVendor = doesBranchPrivacyVendorExist ? tagBranch?.privacy[0]?.vendor?.label : null;
        if ((masterPrivacyVendor || branchPrivacyVendor) && masterPrivacyVendor !== branchPrivacyVendor) {
          accordionData.fields.push(
            createFieldData({
              label: t('diffModal.tags.privacy_vendor'),
              type: 'text',
              master: masterPrivacyVendor,
              branch: branchPrivacyVendor,
            })
          );
        }
      }

      // consent re-ask
      const consentReAskMaster = (tagMaster?.privacy?.length > 0 ? tagMaster?.privacy[0] : null)?.minor_tag;
      const consentReAskBranch = (tagBranch?.privacy?.length > 0 ? tagBranch?.privacy[0] : null)?.minor_tag;
      if (isValidNumber(consentReAskMaster) || isValidNumber(consentReAskBranch)) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.consent_re_ask'),
            type: 'checkbox',
            master: consentReAskMaster === 0 ? false : consentReAskMaster === 1 ? true : null,
            branch: consentReAskBranch === 0 ? false : consentReAskBranch === 1 ? true : null,
          })
        );
      }

      // expiration date
      const expirationDateMaster = moment(tagMaster?.date_expiration, moment.ISO_8601, true).isValid()
        ? moment(tagMaster?.date_expiration).format('YYYY/MM/DD')
        : null;
      const expirationDateBranch = moment(tagBranch?.date_expiration, moment.ISO_8601, true).isValid()
        ? moment(tagBranch?.date_expiration).format('YYYY/MM/DD')
        : null;
      if (expirationDateMaster || expirationDateBranch) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.expiration_date'),
            type: 'text',
            master: expirationDateMaster,
            branch: expirationDateBranch,
          })
        );
      }

      //timeout
      if (tagBranch?.timeout || tagMaster?.timeout) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.timeout'),
            type: 'text',
            master: tagMaster?.timeout,
            branch: tagBranch?.timeout,
          })
        );
      }

      // order
      if (isValidNumber(tagBranch?.position) || isValidNumber(tagMaster?.position)) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.order'),
            type: 'text',
            master: tagMaster?.position,
            branch: tagBranch?.position,
          })
        );
      }

      // questions / Answers
      if (tagBranch?.answers || tagMaster?.answers) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.questions_answers'),
            type: 'map',
            master: tagMaster?.answers?.map(answer => ({
              key: answer.question_label,
              value: answer.label,
            })),
            branch: tagBranch?.answers?.map(answer => ({
              key: answer.question_label,
              value: answer.label,
            })),
          })
        );
      }
      accordions.push(accordionData);
    });
  });

  return {
    title: t('diffModal.tags.title'),
    accordions,
  };
};

export const generateAccordionForEvents = function(data, t) {
  const accordions = [];
  data?.tc_event?.forEach(event => {
    handleTryCatch(() => {
      const tagMaster = event.master?.id_tag_event ? event.master : null; // only event with an id_tag_event are considered, the API should return only event with an id_tag_event but it's an extra check
      const tagBranch = event.branch?.id_tag_event ? event.branch : null; // only event with an id_tag_event are considered, the API should return only event with an id_tag_event but it's an extra check

      const statusMaster = getStatusFromAction(tagMaster?.action, tagBranch?.action, Boolean(tagBranch?.id_tag_event));
      const statusBranch = getStatusFromAction(tagBranch?.action, tagMaster?.action, Boolean(tagMaster?.id_tag_event));
      const accordionData = createAccordionData({
        title: `${t('diffModal.tags_events.label')}: ${tagBranch?.label_tag_event ?? tagMaster?.label_tag_event}`,
        elementType: 'tc_event',
        statuses: {
          master: statusMaster,
          branch: statusBranch,
        },
        payloadMerge: getMergePayloadForElementWithIDs({
          statusMaster,
          statusBranch,
          idMaster: tagMaster?.id_tag_event,
          idBranch: tagBranch?.id_tag_event,
        }),
      });

      //Name: Name are always returned by the API so that it can be displayed in the header of the accordion, they need to be compared to avoid displaying them in the fields
      if (
        (tagBranch?.label_tag_event || tagMaster?.label_tag_event) &&
        tagBranch?.label_tag_event !== tagMaster?.label_tag_event
      ) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags_events.name'),
            type: 'text',
            master: tagMaster?.label_tag_event,
            branch: tagBranch?.label_tag_event,
          })
        );
      }

      //ID
      if (tagBranch?.ref_tag_event || tagMaster?.ref_tag_event) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags_events.id'),
            type: 'text',
            master: tagMaster?.ref_tag_event,
            branch: tagBranch?.ref_tag_event,
          })
        );
      }

      //Variables
      if (tagBranch?.variables || tagMaster?.variables) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags_events.variables'),
            type: 'map',
            master: tagMaster?.variables?.map(variable => ({
              key: variable.code,
              value: variable.variable?.slice(1, -1),
            })),
            branch: tagBranch?.variables?.map(variable => ({
              key: variable.code,
              value: variable.variable?.slice(1, -1),
            })),
          })
        );
      }

      // code
      if (tagBranch?.code_tag_event || tagMaster?.code_tag_event) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags_events.code'),
            type: 'code',
            master: tagMaster?.code_tag_event,
            branch: tagBranch?.code_tag_event,
          })
        );
      }

      // tag_cleaner
      if (isValidNumber(tagBranch?.tag_cleaner) || isValidNumber(tagMaster?.tag_cleaner)) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags_events.tag_cleaner'),
            type: 'checkbox',
            master: tagMaster?.tag_cleaner === 0 ? false : tagMaster?.tag_cleaner === 1 ? true : null,
            branch: tagBranch?.tag_cleaner === 0 ? false : tagBranch?.tag_cleaner === 1 ? true : null,
          })
        );
      }

      // privacy category
      if (tagMaster?.privacy?.length > 0 || tagBranch?.privacy?.length > 0) {
        const doesMasterPrivacyCategoryExist = tagMaster?.privacy?.length > 0 && tagMaster?.privacy[0]?.category?.label;
        const masterPrivacyCategory = doesMasterPrivacyCategoryExist ? tagMaster?.privacy[0]?.category?.label : null;

        const doesBranchPrivacyCategoryExist = tagBranch?.privacy?.length > 0 && tagBranch?.privacy[0]?.category?.label;
        const branchPrivacyCategory = doesBranchPrivacyCategoryExist ? tagBranch?.privacy[0]?.category?.label : null;
        if (masterPrivacyCategory || branchPrivacyCategory) {
          accordionData.fields.push(
            createFieldData({
              label: t('diffModal.tags.privacy_category'),
              type: 'text',
              master: masterPrivacyCategory,
              branch: branchPrivacyCategory,
            })
          );
        }
      }

      //constraints
      const constraintsBranch = tagBranch?.rules;
      const constraintsMaster = tagMaster?.rules;
      if (constraintsBranch?.length > 0 || constraintsMaster?.length > 0) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.constraints'),
            type: 'list',
            master: constraintsMaster?.map(constraint => constraint.label_rule),
            branch: constraintsMaster?.map(constraint => constraint.label_rule),
          })
        );
      }

      accordions.push(accordionData);
    });
  });

  return {
    title: t('diffModal.tags_events.title'),
    accordions: accordions.filter(accordion => accordion.fields?.length > 0),
  };
};

export const generateAccordionForRules = function(data, t) {
  const accordions = [];

  const rules = [
    ...(data?.rule?.map(rule => ({ ...rule, elementType: 'rule' })) || []),
    ...(data?.tc_event_rule?.map(rule => ({ ...rule, elementType: 'tc_event_rule' })) || []),
    ...(data?.privacy_rule?.map(rule => ({ ...rule, elementType: 'privacy_rule' })) || []),
  ];

  rules?.forEach(rule => {
    handleTryCatch(() => {
      const ruleMaster = rule?.master?.id_rule ? rule.master : null; // only rule with an id_rule are considered, the API should return only rule with an id_rule but it's an extra check
      const ruleBranch = rule?.branch?.id_rule ? rule.branch : null; // only rule with an id_rule are considered, the API should return only rule with an id_rule but it's an extra check

      const idRuleType = ruleMaster?.id_rule_ref || ruleBranch?.id_rule_ref;
      let ruleCategoryLabel = store.state.tms?.ruleCategory?.entities.find(category =>
        category.ruleType.find(type => type.id == idRuleType)
      )?.label;

      ruleCategoryLabel = ruleCategoryLabel || t('diffModal.rules.advanced');

      const statusMaster = getStatusFromAction(ruleMaster?.action, ruleBranch?.action, Boolean(ruleBranch?.id_rule));
      const statusBranch = getStatusFromAction(ruleBranch?.action, ruleMaster?.action, Boolean(ruleMaster?.id_rule));
      const accordionData = createAccordionData({
        title: `${
          ruleMaster?.perimeter === 1 || ruleBranch?.perimeter === 1
            ? t('diffModal.rules.perimeters')
            : t('diffModal.rules.constraints')
        }-${ruleCategoryLabel}: ${ruleBranch?.label_rule ?? ruleMaster?.label_rule}`,
        elementType: rule.elementType,
        statuses: {
          master: statusMaster,
          branch: statusBranch,
        },
        payloadMerge: getMergePayloadForElementWithIDs({
          statusMaster,
          statusBranch,
          idMaster: ruleMaster?.id_rule,
          idBranch: ruleBranch?.id_rule,
        }),
      });

      //Bin
      if (isValidNumber(ruleBranch?.bin) || isValidNumber(ruleMaster?.bin)) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.rules.bin'),
            type: 'checkbox',
            master: ruleMaster?.bin === 0 ? false : ruleMaster?.bin === 1 ? true : null,
            branch: ruleBranch?.bin === 0 ? false : ruleBranch?.bin === 1 ? true : null,
          })
        );
      }

      //Name
      if ((ruleBranch?.label_rule || ruleMaster?.label_rule) && ruleBranch?.label_rule !== ruleMaster?.label_rule) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.rules.name'),
            type: 'text',
            master: ruleMaster?.label_rule,
            branch: ruleBranch?.label_rule,
          })
        );
      }

      //Description
      if (ruleBranch?.parameters || ruleMaster?.parameters) {
        //remmove first and last character if they are curly brackets and replace the "|" with ","
        let str = ruleMaster?.parameters;
        let parameterObjectMaster =
          str
            ?.replaceAll('{', '')
            .replaceAll('}', '')
            .replaceAll(/\|/g, ',') || null;
        parameterObjectMaster =
          parameterObjectMaster?.charAt(0) === ',' ? parameterObjectMaster.slice(1) : parameterObjectMaster;
        str = ruleBranch?.parameters;
        let parameterObjectBranch =
          str
            ?.replaceAll('{', '')
            .replaceAll('}', '')
            .replaceAll(/\|/g, ',') || null;
        parameterObjectBranch =
          parameterObjectBranch?.charAt(0) === ',' ? parameterObjectBranch.slice(1) : parameterObjectBranch;

        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.rules.description'),
            type: 'text',
            master: parameterObjectMaster,
            branch: parameterObjectBranch,
          })
        );
      }

      //trigger
      if (ruleBranch?.trigger?.length > 0 || ruleMaster?.trigger?.length > 0) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.rules.trigger'),
            type: 'text',
            master: ruleMaster?.trigger[0]?.label,
            branch: ruleBranch?.trigger[0]?.label,
          })
        );
      }

      accordions.push(accordionData);
    });
  });

  // deduplication
  data?.deduplication_rule?.forEach(deduplication => {
    handleTryCatch(() => {
      const deduplicationMaster = deduplication.master?.id_condition ? deduplication.master : null;
      const deduplicationBranch = deduplication.branch?.id_condition ? deduplication.branch : null;

      const statusMaster = getStatusFromAction(
        deduplicationMaster?.action,
        deduplicationBranch?.action,
        deduplicationBranch?.id_condition
      );

      const statusBranch = getStatusFromAction(
        deduplicationBranch?.action,
        deduplicationMaster?.action,
        deduplicationMaster?.id_condition
      );

      const accordionData = createAccordionData({
        title: `${t('diffModal.rules.deduplication')}: ${deduplicationBranch?.tag[0]?.label_tag ??
          deduplicationMaster?.tag[0]?.label_tag}`,
        elementType: 'deduplication_rule',
        statuses: {
          master: statusMaster,
          branch: statusBranch,
        },
        payloadMerge: getMergePayloadForElementWithIDs({
          statusMaster,
          statusBranch,
          idMaster: deduplicationMaster?.id_condition,
          idBranch: deduplicationBranch?.id_condition,
        }),
      });

      //condition_type
      if (deduplicationMaster?.condition_type || deduplicationBranch?.condition_type) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.condition_type'),
            type: 'text',
            master: deduplicationMaster?.condition_type,
            branch: deduplicationBranch?.condition_type,
          })
        );
      }

      // touch_type
      if (deduplicationMaster?.touch_type || deduplicationBranch?.touch_type) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.touch_type'),
            type: 'text',
            master: deduplicationMaster?.touch_type,
            branch: deduplicationBranch?.touch_type,
          })
        );
      }

      // touch
      if (deduplicationMaster?.touch || deduplicationBranch?.touch) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.touch'),
            type: 'text',
            master: deduplicationMaster?.touch?.toLowerCase(),
            branch: deduplicationBranch?.touch?.toLowerCase(),
          })
        );
      }
      //is_not_from_channel
      if (
        isValidNumber(deduplicationMaster?.is_not_from_channel) ||
        isValidNumber(deduplicationBranch?.is_not_from_channel)
      ) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.is_not_from_channel'),
            type: 'text',
            master:
              deduplicationMaster?.is_not_from_channel === 1
                ? t('diffModal.tags.is_not')
                : deduplicationMaster?.is_not_from_channel === 0
                ? t('diffModal.tags.is')
                : null,
            branch:
              deduplicationBranch?.is_not_from_channel === 1
                ? t('diffModal.tags.is_not')
                : deduplicationBranch?.is_not_from_channel === 0
                ? t('diffModal.tags.is')
                : null,
          })
        );
      }
      //channels
      if (deduplicationMaster?.channels?.length > 0 || deduplicationBranch?.channels?.length > 0) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.channels'),
            type: 'list',
            master: deduplicationMaster?.channels?.map(channel => channel.label_channel),
            branch: deduplicationBranch?.channels?.map(channel => channel.label_channel),
          })
        );
      }

      //source
      if (deduplicationMaster?.source || deduplicationBranch?.source) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.source'),
            type: 'text',
            master: deduplicationMaster?.source,
            branch: deduplicationBranch?.source,
          })
        );
      }

      //source_value
      if (deduplicationMaster?.source_value || deduplicationBranch?.source_value) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.tags.source_value'),
            type: 'text',
            master: deduplicationMaster?.source_value,
            branch: deduplicationBranch?.source_value,
          })
        );
      }

      accordions.push(accordionData);
    });
  });

  data?.trigger?.forEach(trigger => {
    handleTryCatch(() => {
      const ruleMaster = trigger?.master?.id_trigger ? trigger.master : null;
      const ruleBranch = trigger?.branch?.id_trigger ? trigger.branch : null;

      const idRuleCategory = ruleMaster?.id_category || ruleBranch?.id_category;
      let ruleCategoryLabel = store.state.tms?.triggerCategory?.entities.find(category => category.id == idRuleCategory)
        ?.label;

      ruleCategoryLabel = ruleCategoryLabel || t('diffModal.rules.containerLoaded');

      const statusMaster = getStatusFromAction(ruleMaster?.action, ruleBranch?.action, ruleBranch?.id_trigger);
      const statusBranch = getStatusFromAction(ruleBranch?.action, ruleMaster?.action, ruleMaster?.id_trigger);
      const accordionData = createAccordionData({
        title: `${t('diffModal.rules.trigger')}-${ruleCategoryLabel}: ${ruleBranch?.label ?? ruleMaster?.label}`,
        elementType: 'trigger',
        statuses: {
          master: statusMaster,
          branch: statusBranch,
        },
        payloadMerge: getMergePayloadForElementWithIDs({
          statusMaster,
          statusBranch,
          idMaster: ruleMaster?.id_trigger,
          idBranch: ruleBranch?.id_trigger,
        }),
      });

      //Bin
      if (isValidNumber(ruleBranch?.bin) || isValidNumber(ruleMaster?.bin)) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.rules.bin'),
            type: 'checkbox',
            master: ruleMaster?.bin === 0 ? false : ruleMaster?.bin === 1 ? true : null,
            branch: ruleBranch?.bin === 0 ? false : ruleBranch?.bin === 1 ? true : null,
          })
        );
      }

      //Name
      if (ruleBranch?.label || ruleMaster?.label) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.rules.name'),
            type: 'text',
            master: ruleMaster?.label,
            branch: ruleBranch?.label,
          })
        );
      }

      //Selector
      if (ruleBranch?.css_selector || ruleMaster?.css_selector) {
        accordionData.fields.push(
          createFieldData({
            label: t(`diffModal.rules.selector${idRuleCategory === 2 ? 'Click' : 'Form'}`),
            type: 'text',
            master: ruleMaster?.css_selector,
            branch: ruleBranch?.css_selector,
          })
        );
      }

      //Parameters
      if (ruleBranch?.parameters || ruleMaster?.parameters) {
        const parameterObjectMaster = ruleMaster?.parameters ? JSON.parse(ruleMaster?.parameters) : null;
        const parameterObjectBranch = ruleBranch?.parameters ? JSON.parse(ruleBranch?.parameters) : null;

        // fucntion name
        if (parameterObjectMaster?.function || parameterObjectBranch?.function) {
          accordionData.fields.push(
            createFieldData({
              label: t('diffModal.rules.functionName'),
              type: 'text',
              master: parameterObjectMaster?.function,
              branch: parameterObjectBranch?.function,
            })
          );
        }

        //scroll
        if (parameterObjectMaster?.scroll?.length > 0 || parameterObjectBranch?.scroll?.length > 0) {
          accordionData.fields.push(
            createFieldData({
              label: t('diffModal.rules.scroll'),
              type: 'list',
              master: parameterObjectMaster?.scroll.map(
                scroll =>
                  `${scroll.type === 'scrollX' ? 'horizontal' : 'vertical'}, ${scroll.value} ${
                    scroll['value-type'] === 'pixels' ? 'px' : '%'
                  }`
              ),
              branch: parameterObjectBranch?.scroll.map(
                scroll =>
                  `${scroll.type === 'scrollX' ? 'horizontal' : 'vertical'}, ${scroll.value} ${
                    scroll['value-type'] === 'pixels' ? 'px' : '%'
                  }`
              ),
            })
          );
        }
      }

      accordions.push(accordionData);
    });
  });

  return { title: t('diffModal.rules.title'), accordions };
};

export const generateAccordionForCustomJs = function(data, t) {
  const accordions = [];

  // custom_js
  if (data?.custom_js) {
    handleTryCatch(() => {
      const customJsBefore = data.custom_js.find(js => js.master.flag_type === 1 || js.branch.flag_type === 1);
      const customJsAfter = data.custom_js.find(js => js.master.flag_type === 0 || js.branch.flag_type === 0);
      const customJsBeforeMaster = customJsBefore?.master?.code_input;
      const customJsBeforeBranch = customJsBefore?.branch?.code_input;
      const customJsAfterMaster = customJsAfter?.master?.code_input;
      const customJsAfterBranch = customJsAfter?.branch?.code_input;

      const statusMaster = getStatusFromAction(customJsBefore?.master?.action, customJsBefore?.branch?.action, true); // customJs always exists so it's always true
      const statusBranch = getStatusFromAction(customJsBefore?.branch?.action, customJsBefore?.master?.action, true); // customJs always exists so it's always true
      const customJsAccordionData = createAccordionData({
        title: t('diffModal.custom_js.static_javascript'),
        elementType: 'config',
        statuses: {
          master: statusMaster,
          branch: statusBranch,
        },
        payloadMerge: {
          masterMerge: ['custom_js'],
          branchMerge: ['custom_js'],
        },
      });

      if (customJsBeforeMaster || customJsBeforeBranch) {
        customJsAccordionData.fields.push(
          createFieldData({
            label: t('diffModal.custom_js.dynamicBefore'),
            type: 'code',
            master: customJsBeforeMaster,
            branch: customJsBeforeBranch,
          })
        );
      }

      if (customJsAfterMaster || customJsAfterBranch) {
        customJsAccordionData.fields.push(
          createFieldData({
            label: t('diffModal.custom_js.dynamicAfter'),
            type: 'code',
            master: customJsAfterMaster,
            branch: customJsAfterBranch,
          })
        );
      }

      accordions.push(customJsAccordionData);
    });
  }

  return { title: t('diffModal.custom_js.title'), accordions };
};

export const generateAccordionConfig = function(data, t) {
  if (!data?.config) return { title: t('diffModal.config.title'), accordions: [] };
  const accordions = [];
  handleTryCatch(() => {
    const possiblePayloadValues = [
      'label_tagcommander',
      'mapped_sync_mode',
      'mapped_expiration_users',
      'deploymentMailingList',
      'expiration_date',
      'triggers',
      'noscript',
      'md5_verification',
      'jquery_test',
      'cookie_samesite_setting',
      'cookie_secure_setting',
      'privacies',
      'dynamic_js',
    ];
    const configMaster = data?.config?.master;
    const configBranch = data?.config?.branch;

    const allAffectedElements = possiblePayloadValues.filter(
      value => configMaster[value] !== undefined || configBranch[value] !== undefined
    );

    if (configMaster?.privacies?.length > 0 || configBranch?.privacies?.length > 0)
      allAffectedElements.push('privacies');

    const accordionData = createAccordionData({
      title: t('diffModal.config.title'),
      elementType: 'config',
      statuses: {
        master: getStatusFromAction(configMaster?.action, configBranch?.action, true), // config always exists so it's always true
        branch: getStatusFromAction(configBranch?.action, configMaster?.action, true), // config always exists so it's always true
      },
      payloadMerge: {
        masterMerge: allAffectedElements,
        branchMerge: allAffectedElements,
      },
    });

    // label_tagcommander
    if (configBranch.label_tagcommander || configMaster.label_tagcommander) {
      accordionData.fields.push(
        createFieldData({
          label: t('containerSettings.general.name'),
          type: 'text',
          master: configMaster.label_tagcommander,
          branch: configBranch.label_tagcommander,
        })
      );
    }

    // sync_mode
    const mappingSyncModeLabel = {
      email: 'containerSettings.synchronization.sendEmail',
      manual: 'containerSettings.synchronization.manual',
      batch: 'containerSettings.synchronization.updateBatch',
      ftp_or_cdn: 'containerSettings.synchronization.ftpCdn',
      custom_url: 'containerSettings.synchronization.customUrl',
      unminified: 'containerSettings.synchronization.unminified',
      amazon_s3: 'containerSettings.synchronization.amazonS3',
    };
    if (configBranch?.mapped_sync_mode || configMaster?.mapped_sync_mode) {
      const syncModeMaster = configMaster?.mapped_sync_mode ? Object.values(configMaster?.mapped_sync_mode) : [];
      const syncModeBranch = configBranch?.mapped_sync_mode ? Object.values(configBranch?.mapped_sync_mode) : [];
      accordionData.fields.push(
        createFieldData({
          label: t('containerSettings.synchronization.mode'),
          type: 'map-checkbox',
          master: Object.keys(mappingSyncModeLabel).map(key => ({
            key: t(mappingSyncModeLabel[key]),
            value: syncModeMaster.includes(key),
          })),
          branch: Object.keys(mappingSyncModeLabel).map(key => ({
            key: t(mappingSyncModeLabel[key]),
            value: syncModeBranch?.includes(key),
          })),
        })
      );
    }

    // expiration users
    if (configBranch?.deploymentMailingList?.length > 0 || configMaster?.deploymentMailingList?.length > 0) {
      accordionData.fields.push(
        createFieldData({
          label: t('containerSettings.synchronization.synchronizationEmail'),
          type: 'list',
          master: configMaster.deploymentMailingList?.map(user => user?.email_user),
          branch: configBranch.deploymentMailingList?.map(user => user?.email_user),
        })
      );
    }

    // map-checkbox of noscript md5_verification jquery_test
    const mappingOptionsLabel = {
      noscript: 'containerSettings.advanced.noScript',
      md5_verification: 'containerSettings.advanced.md5',
      jquery_test: 'containerSettings.advanced.jQuery',
    };

    const masterOptions = Object.keys(mappingOptionsLabel)
      .map(key => ({
        key: t(mappingOptionsLabel[key]),
        value: configMaster[key] == 0 ? false : configMaster[key] == 1 ? true : null,
      }))
      ?.filter(option => option.value !== null);

    const branchOptions = Object.keys(mappingOptionsLabel)
      .map(key => ({
        key: t(mappingOptionsLabel[key]),
        value: configBranch[key] == 0 ? false : configBranch[key] == 1 ? true : null,
      }))
      ?.filter(option => option.value !== null);

    if (masterOptions.some(option => option.value !== null) || branchOptions.some(option => option.value !== null)) {
      accordionData.fields.push({
        label: t('containerSettings.advanced.options'),
        type: 'map-checkbox',
        master: masterOptions,
        branch: branchOptions,
      });
    }

    // expiration_date
    if (configBranch?.expiration_date || configMaster?.expiration_date) {
      accordionData.fields.push(
        createFieldData({
          label: t('containerSettings.advanced.expirationDate'),
          type: 'checkbox',
          master: configMaster.expiration_date !== 0,
          branch: configBranch.expiration_date !== 0,
        })
      );

      const mappingExpirationDateLabel = {
        3: 'containerSettings.advanced.3months',
        6: 'containerSettings.advanced.6months',
        12: 'containerSettings.advanced.1year',
      };
      if (configBranch.expiration_date !== 0 || configMaster.expiration_date !== 0) {
        accordionData.fields.push(
          createFieldData({
            label: t('containerSettings.advanced.expirationDateDuration'),
            type: 'text',
            master: configMaster.expiration_date ? t(mappingExpirationDateLabel[configMaster.expiration_date]) : null,
            branch: configBranch.expiration_date ? t(mappingExpirationDateLabel[configBranch.expiration_date]) : null,
          })
        );
      }
    }

    // expiration users
    if (configBranch?.mapped_expiration_users?.length > 0 || configMaster?.mapped_expiration_users?.length > 0) {
      accordionData.fields.push(
        createFieldData({
          label: t('containerSettings.advanced.sendReport'),
          type: 'list',
          master: configMaster.mapped_expiration_users?.map(user => user?.email_user),
          branch: configBranch.mapped_expiration_users?.map(user => user?.email_user),
        })
      );
    }

    // triggers
    if (configBranch?.triggers || configMaster?.triggers) {
      accordionData.fields.push(
        createFieldData({
          label: t('containerSettings.advanced.containerTriggers'),
          type: 'list',
          master: configMaster.triggers.map(trigger => trigger?.label),
          branch: configBranch.triggers.map(trigger => trigger?.label),
        })
      );
    }

    // cookie_samesite_setting
    if (configBranch.cookie_samesite_setting || configMaster.cookie_samesite_setting) {
      accordionData.fields.push(
        createFieldData({
          label: t('containerSettings.advanced.samesiteSettings'),
          type: 'text',
          master: configMaster.cookie_samesite_setting
            ? configMaster.cookie_samesite_setting
            : t('containerSettings.advanced.doNotForce'),
          branch: configBranch.cookie_samesite_setting
            ? configBranch.cookie_samesite_setting
            : t('containerSettings.advanced.doNotForce'),
        })
      );
    }

    // cookie_secure_setting
    const getLabel = value => {
      if (value === false) return t('common.disabled');
      if (value === true) return t('common.enabled');
      return t('containerSettings.advanced.doNotForce');
    };

    if (configBranch?.cookie_secure_setting !== undefined || configMaster?.cookie_secure_setting !== undefined) {
      accordionData.fields.push(
        createFieldData({
          label: t('containerSettings.advanced.secureSettings'),
          type: 'text',
          master: getLabel(configMaster.cookie_secure_setting),
          branch: getLabel(configBranch.cookie_secure_setting),
        })
      );
    }

    //privacies
    if (configMaster?.privacies?.length > 0 || configBranch?.privacies?.length > 0) {
      const allPrivacies = [...(configMaster?.privacies || []), ...(configBranch?.privacies || [])];

      const allPrivaciesNosDuplicates = allPrivacies.filter(
        (privacy, index, self) => index === self.findIndex(t => t.id_privacy === privacy.id_privacy)
      );

      const privaciesMaster = allPrivaciesNosDuplicates.map(privacy => ({
        key: privacy?.label,
        value: Boolean(configMaster?.privacies?.find(p => p.id_privacy === privacy.id_privacy)),
        id_privacy: privacy.id_privacy,
      }));

      const privaciesBranch = allPrivaciesNosDuplicates.map(privacy => ({
        key: privacy?.label,
        value: Boolean(configBranch?.privacies?.find(p => p.id_privacy === privacy.id_privacy)),
        id_privacy: privacy.id_privacy,
      }));

      // remove privacies with same master and branch value so that we only display the different ones
      const privaciesMasterOnlyChangedValue = privaciesMaster.filter(
        privacy => privaciesBranch.find(p => p.id_privacy === privacy.id_privacy)?.value !== privacy?.value
      );
      const privaciesBranchOnlyChangedValue = privaciesBranch.filter(
        privacy => privaciesMaster.find(p => p.id_privacy === privacy.id_privacy)?.value !== privacy?.value
      );

      if (privaciesMaster?.length > 0 || privaciesBranch?.length > 0) {
        accordionData.fields.push(
          createFieldData({
            label: t('containerSettings.advanced.privacies'),
            type: 'map-checkbox',
            master: privaciesMasterOnlyChangedValue,
            branch: privaciesBranchOnlyChangedValue,
          })
        );
      }
    }

    // dynamic_js
    const dynamicJsMaster = data?.dynamic_js?.master || configMaster?.dynamic_js;
    const dynamicJsBranch = data?.dynamic_js?.branch || configBranch?.dynamic_js;
    if (dynamicJsMaster || dynamicJsBranch) {
      const dynamicJsMasterBefore = dynamicJsMaster?.filter(js => js.id_block === 1);
      const dynamicJsMasterAfter = dynamicJsMaster?.filter(js => js.id_block === 2);

      const dynamicJsBranchBefore = dynamicJsBranch?.filter(js => js.id_block === 1);
      const dynamicJsBranchAfter = dynamicJsBranch?.filter(js => js.id_block === 2);
      if (dynamicJsMasterBefore?.length || dynamicJsBranchBefore?.length) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.custom_js.dynamicBefore'),
            type: 'list',
            master: dynamicJsMasterBefore.map(js => js.url_file)?.length
              ? dynamicJsMasterBefore.map(js => js.url_file)
              : null,
            branch: dynamicJsBranchBefore.map(js => js.url_file)?.length
              ? dynamicJsBranchBefore.map(js => js.url_file)
              : null,
          })
        );
      }
      if (dynamicJsMasterAfter?.length || dynamicJsBranchAfter?.length) {
        accordionData.fields.push(
          createFieldData({
            label: t('diffModal.custom_js.dynamicAfter'),
            type: 'list',
            master: dynamicJsMasterAfter.map(js => js.url_file)?.length
              ? dynamicJsMasterAfter.map(js => js.url_file)
              : [],
            branch: dynamicJsBranchAfter.map(js => js.url_file)?.length
              ? dynamicJsBranchAfter.map(js => js.url_file)
              : [],
          })
        );
      }
    }
    accordions.push(accordionData);
  });
  return { title: t('diffModal.config.title'), accordions };
};
